import BlockyHeavyText from "components/custom-texts/blocky-heavy-text.tsx";
import BlockyLightText from "components/custom-texts/blocky-light-text";
import styles from "./styles.module.css";
import { POOL_STATUS, secondaryColor } from "utils/constants.ts";
import { EntryFeeDisplay, PrizeDisplay, PrizeValueDisplay } from "./shared/contest-displays";
import TimeDisplay from "./shared/time-display.tsx";
import CountdownClock from "components/pool-item/countdown-clock.tsx";
import StatusBadge from "components/status-badge/pool-status-badge.tsx";
import ContestImage from "./shared/contest-displays/contest-image/contest-image.tsx";
import { Contest, ContestEntry } from "interfaces/leaderboard/contest";
import ContestEntryStatusIcon from "components/contests/contest-display-items/entry-status";
import { useAppSelector } from "state/hooks.ts";
import { getContestEntryById, getCurrentEntryIdByContestId } from "state/slices/contests-slice.ts";

interface Props {
  contest: Contest;
  userContestEntries: ContestEntry[];
  showStatus?: boolean;
  entryFeeOverride?: number;
}

const ContestItem = ({ contest, userContestEntries, showStatus, entryFeeOverride}: Props) => {

  const currentContestEntryId = useAppSelector(state => getCurrentEntryIdByContestId(state, contest.id));

  const currentContestEntry = useAppSelector(
    state => getContestEntryById(state, currentContestEntryId));

  const {
    max_entries,
    max_entries_per_user,
    total_entries,
    total_prize_value,
    name,
    headline
  } = contest;

  const EntriesDisplay = () => {
    const userEntriesCount = userContestEntries.length;

    // Line 1: total/max Entries (or just total if unlimited)
    const totalEntriesText = max_entries
      ? `${total_entries}/${max_entries} Entries`
      : `${total_entries} Entries`;

    // Line 2: (user/max) (use ∞ if unlimited)
    const userEntriesText = max_entries_per_user
      ? `(${userEntriesCount}/${max_entries_per_user})`
      : `(${userEntriesCount}/∞)`;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <BlockyLightText style={{
          color: "var(--sparket-off-white)",
          fontSize: 12,
          whiteSpace: "nowrap",
        }}>
          {totalEntriesText}
        </BlockyLightText>
        <BlockyLightText style={{
          color: "var(--sparket-off-white)",
          fontSize: 12,
        }}>
          {userEntriesText}
        </BlockyLightText>
      </div>
    );
  };

  return (
    <div
      style={{
        borderRadius: 10,
        display: "flex",
        boxShadow: "0px 0px 8px #f2f2f5",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <ContestImage url={contest.settings.logo_url} type={contest.type} />
      <div
        style={{ margin: "10px 10px 10px 10px" }}
      >
        {/* title/headline */}

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {/* left panel empty */}
          {showStatus ? (
            <StatusBadge
              size="sm"
              customStyles={{ marginTop: 5 }}
              status={
                POOL_STATUS[
                  contest.state.toUpperCase() as keyof typeof POOL_STATUS
                  ]
              }
            />
          ) : (
            <div style={{ width: 35 }} />
          )}

          {/* Middle panel is title/headline */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <BlockyHeavyText style={{ fontSize: 16, color: secondaryColor }}>
              {name}
            </BlockyHeavyText>
            <BlockyHeavyText
              style={{
                fontSize: 11,
                color: "var(--sparket-off-white)",
              }}
            >
              {headline}
            </BlockyHeavyText>
          </div>

          {/* right panel */}
          <PrizeDisplay contest={contest} />
        </div>

        {/* middle row */}
        <div className={styles.contestDetailsWrapper}>
          <div className={styles.bottomColumn}>
            <TimeDisplay endAt={contest.end_at} />
            <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              <CountdownClock close={new Date(contest.end_at)} />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <EntryFeeDisplay entryFee={entryFeeOverride ?? 0} />
            {total_prize_value && <PrizeValueDisplay prizeValue={total_prize_value} contest={contest} />}
          </div>
          <div
            style={{
              alignSelf: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            {currentContestEntry && <ContestEntryStatusIcon contestEntry={currentContestEntry} />}
            <EntriesDisplay />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContestItem;
